import React, { useState, useEffect } from 'react';
import NextLink from 'next/link';
import styled from '@emotion/styled';
import { pathToRegexp } from 'path-to-regexp';
import { CommonStyles } from '@teko/ds-desktop';
import { getRouteRegex } from 'next/dist/shared/lib/router/utils/route-regex';
import { getRouteMatcher } from 'next/dist/shared/lib/router/utils/route-matcher';
import { getClientBuildManifest } from 'next/dist/client/route-loader';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const serverless = typeof window !== "undefined";
const normalizeUrl = (url) => {
  if (!serverless || !url || typeof url !== "string")
    return url;
  if (url.match(/^https?:\/\/([^/]+)/)) {
    const urlObj = new URL(url);
    if (urlObj.hostname === window.location.host) {
      return `${urlObj.pathname}${urlObj.search}`;
    }
  }
  return url;
};
const TekoLink = (_a) => {
  var _b = _a, {
    children,
    href,
    as,
    prefetch = false
  } = _b, rest = __objRest(_b, [
    "children",
    "href",
    "as",
    "prefetch"
  ]);
  const [newState, setNewState] = useState({
    pathname: (typeof href === "string" ? href : href == null ? void 0 : href.pathname) || "",
    query: {},
    as
  });
  useEffect(() => {
    async function init() {
      if (!href)
        return;
      href = normalizeUrl(href);
      as = normalizeUrl(as);
      if (typeof href === "string") {
        const result = await resolveTekoPathname(getPathname(href));
        if (!result.pathname) {
          result.pathname = href;
        }
        setNewState(__spreadProps(__spreadValues({}, result), { as: as || href }));
      } else {
        const result = await resolveTekoPathname(href);
        if (!result.pathname && (href == null ? void 0 : href.pathname)) {
          result.pathname = href.pathname;
        }
        setNewState(__spreadProps(__spreadValues(__spreadValues({}, href), result), {
          as: as || ((href == null ? void 0 : href.pathname) ? href : null) || void 0
        }));
      }
    }
    init();
  }, [href, as, setNewState]);
  if (!href) {
    return /* @__PURE__ */ React.createElement(React.Fragment, null, children);
  }
  if (serverless) {
    return /* @__PURE__ */ React.createElement(StyledLink, __spreadValues({
      "data-testid": "TekoLink",
      href: { pathname: newState.pathname, query: newState.query },
      as: newState.as,
      prefetch
    }, rest), children);
  }
  return /* @__PURE__ */ React.createElement(StyledLink, __spreadProps(__spreadValues({
    "data-testid": "TekoLink",
    href: href || "",
    as
  }, rest), {
    prefetch
  }), children);
};
const pagePaths = [];
async function getSortedPages() {
  const pages = (await getClientBuildManifest()).sortedPages;
  if (pagePaths.length === 0) {
    const defaultPaths = pages.filter((p) => p.includes("/default/"));
    const customPaths = pages.filter((p) => !p.includes("/default/"));
    pagePaths.push(...customPaths, ...defaultPaths);
  }
  return pagePaths;
}
async function resolveTekoPathname(url) {
  var _a;
  const newHref = { pathname: "", query: {} };
  const pageList = await getSortedPages();
  for (let i = 0; i < (customizedRoutes == null ? void 0 : customizedRoutes.length); ++i) {
    const { as, href } = customizedRoutes[i];
    const keys = [];
    const regex = pathToRegexp(as, keys);
    if (regex.test(url.pathname)) {
      const params = regex.exec(url.pathname);
      const combinedObject = Object.fromEntries(keys.map((k, i2) => [k.name, params == null ? void 0 : params[i2 + 1]]));
      const filteredObject = {};
      for (const k in combinedObject) {
        if (!/^[0-9]*$/g.test(k.toString())) {
          filteredObject[k] = combinedObject[k];
        }
      }
      const query = filteredObject;
      newHref.pathname = href;
      newHref.query = __spreadValues(__spreadValues(__spreadValues({}, query), getQuerystringObject(url.searchParams)), url.query);
      return newHref;
    }
  }
  for (let i = 0; i < pageList.length; ++i) {
    const normalizedRoute = (_a = /\/(.*)\/(mobile|desktop)(.*)/.exec(pageList[i])) == null ? void 0 : _a[3];
    if (normalizedRoute) {
      const routeRegex = getRouteRegex(normalizedRoute);
      const matcher = getRouteMatcher(routeRegex)(url.pathname);
      if (matcher) {
        newHref.pathname = pageList[i];
        newHref.query = __spreadValues(__spreadValues(__spreadValues({}, matcher), getQuerystringObject(url.searchParams)), url.query);
        return newHref;
      }
    }
  }
  return newHref;
}
function getQuerystringObject(qs) {
  const ret = {};
  if (!qs)
    return ret;
  for (const p of qs) {
    ret[p[0]] = p[1];
  }
  return ret;
}
const StyledLink = styled(NextLink)`
  ${CommonStyles}
  color: black;
  text-decoration: none;
`;
function getPathname(href) {
  try {
    return new URL(href);
  } catch (e) {
    if (!href.startsWith("/")) {
      href = "/" + href;
    }
    return new URL(`https://fake.com${href}`);
  }
}
const contentSlugs = [
  "chinh-sach-bao-hanh",
  "huong-dan-xem-bao-cao-danh-cho-bo-cong-thuong",
  "chinh-sach-bao-mat",
  "chinh-sach-bao-mat-thanh-toan",
  "chinh-sach-bao-mat-thong-tin",
  "chinh-sach-bao-mat-thong-tin-ca-nhan",
  "chinh-sach-bao-mat-va-chia-se-thong-tin",
  "chinh-sach-bao-mat-va-su-dung",
  "chinh-sach-chiet-khau-uu-dai-mua-sam",
  "chinh-sach-doi-tra",
  "chinh-sach-doi-tra-va-hoan-tien",
  "chinh-sach-giai-quyet-khieu-nai",
  "chinh-sach-giao-hang",
  "chinh-sach-giao-nhan",
  "chinh-sach-kiem-hang",
  "chinh-sach-thanh-toan",
  "chinh-sach-thanh-vien",
  "chinh-sach-the-thanh-vien",
  "chinh-sach-uu-dai",
  "chinh-sach-van-chuyen",
  "chinh-sach-van-chuyen-va-giao-nhan",
  "co-che-dang-tai-thong-tin",
  "danh-sach-cua-hang",
  "dieu-khoan-su-dung",
  "dieu-khoan-va-dieu-kien-giao-dich",
  "gioi-thieu",
  "he-thong-thoi-vang-tich-luy",
  "hinh-thuc-giao-hang",
  "hinh-thuc-thanh-toan",
  "huong-dan-dang-ky-tai-khoan",
  "huong-dan-dat-hang",
  "huong-dan-dat-hang-tren-website",
  "huong-dan-dat-hang-va-dieu-khoan-hop-dong",
  "huong-dan-su-dung",
  "huong-dan-thanh-toan-vnpayqr",
  "quan-ly-chat-luong",
  "quy-che-cung-cap-dich-vu",
  "quy-dinh-va-hinh-thuc-thanh-toan",
  "thong-tin-lien-he",
  "thong-tin-san-pham",
  "thong-tin-ve-dieu-kien-giao-dich"
];
const customizedRoutes = [
  {
    as: "/landing/:id.html",
    href: "/landing/[id]"
  },
  {
    as: "/(.*)-cat.:categoryId([A-Za-z0-9._-]{1,20})",
    href: "/block-categories/[categoryId]"
  },
  {
    as: "/(.*)-scat.:sellerCategoryId([A-Za-z0-9._-]{1,20})",
    href: "/seller-categories/[sellerCategoryId]"
  },
  {
    as: "/(.*)-sel.:sellerId([A-Za-z0-9._-]{1,20})",
    href: "/sellers/[sellerId]"
  },
  {
    as: "/(.*)-brand.:brandId([A-Za-z0-9._-]{1,20})",
    href: "/brands/[brandId]"
  },
  {
    as: "/(.*)-s:sku([A-Za-z0-9._-]{1,20}).html",
    href: "/products/[sku]"
  },
  {
    as: "/(.*)--s:sku([A-Za-z0-9._-]{1,20})",
    href: "/products/[sku]"
  },
  {
    as: "/(.*)--p:productId([A-Za-z0-9._-]{1,20})",
    href: "/products/detail/[productId]"
  },
  {
    as: "/(.*)-:menuItemId.cat",
    href: "/categories/[menuItemId]"
  },
  {
    as: "/(.*)-c:menuItemId([0-9]{1,})",
    href: "/categories/[menuItemId]"
  },
  {
    as: `/:id(${contentSlugs.join("|")})`,
    href: "/content/[id]"
  }
];

export { TekoLink };
